import { useState } from 'react';
import { Plus, Trash2, Users, Crown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useProjectMembers, useAddProjectMember, useRemoveProjectMember } from '@/hooks/useProjectMembers';
import { useTeamMembers } from '@/hooks/useTeamMembers';
import type { ProjectMember } from '@/hooks/useProjectMembers';

interface ProjectMembersSectionProps {
  projectId: string;
}

export function ProjectMembersSection({ projectId }: ProjectMembersSectionProps) {
  const { data: members, isLoading } = useProjectMembers(projectId);
  const { data: teamMembers } = useTeamMembers();
  const addMember = useAddProjectMember();
  const removeMember = useRemoveProjectMember();

  const [isAdding, setIsAdding] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState('');
  const [selectedRole, setSelectedRole] = useState<'member' | 'lead'>('member');
  const [removeTarget, setRemoveTarget] = useState<ProjectMember | null>(null);

  // Filter team members to only show those not already on the project
  const availableMembers = (teamMembers || []).filter(
    (tm) => !(members || []).some((pm) => pm.user_email === tm.email)
  );

  const handleAdd = async () => {
    if (!selectedEmail) return;

    const teamMember = teamMembers?.find((tm) => tm.email === selectedEmail);

    await addMember.mutateAsync({
      projectId,
      data: {
        user_email: selectedEmail,
        user_name: teamMember?.name || undefined,
        role: selectedRole,
      },
    });

    setSelectedEmail('');
    setSelectedRole('member');
    setIsAdding(false);
  };

  const handleRemove = async () => {
    if (!removeTarget) return;
    await removeMember.mutateAsync({
      projectId,
      userEmail: removeTarget.user_email,
    });
    setRemoveTarget(null);
  };

  if (isLoading) {
    return <div className="text-sm text-muted-foreground py-4">Loading team...</div>;
  }

  const membersList = members || [];

  return (
    <div className="space-y-3" data-testid="project-members-section">
      {membersList.length === 0 && !isAdding && (
        <p className="text-sm text-muted-foreground py-2">
          No team members assigned. Add members to track who's working on this project.
        </p>
      )}

      {membersList.length > 0 && (
        <div className="space-y-1">
          {membersList.map((member) => (
            <div
              key={member.id}
              className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-muted/50 group"
              data-testid={`project-member-${member.user_email}`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  {member.role === 'lead' ? (
                    <Crown className="w-3.5 h-3.5 text-amber-600" />
                  ) : (
                    <Users className="w-3.5 h-3.5 text-primary" />
                  )}
                </div>
                <div className="min-w-0">
                  <span className="text-sm font-medium truncate block">
                    {member.user_name || member.user_email}
                  </span>
                  {member.user_name && (
                    <span className="text-xs text-muted-foreground truncate block">
                      {member.user_email}
                    </span>
                  )}
                </div>
                {member.role === 'lead' && (
                  <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">
                    Lead
                  </span>
                )}
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="opacity-0 group-hover:opacity-100 h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                onClick={() => setRemoveTarget(member)}
                data-testid={`remove-member-${member.user_email}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {isAdding ? (
        <div className="space-y-2 p-3 border rounded-md bg-muted/30">
          <Select value={selectedEmail} onValueChange={setSelectedEmail}>
            <SelectTrigger data-testid="member-email-select">
              <SelectValue placeholder="Select team member..." />
            </SelectTrigger>
            <SelectContent>
              {availableMembers.length === 0 ? (
                <SelectItem value="__none__" disabled>
                  All team members already assigned
                </SelectItem>
              ) : (
                availableMembers.map((tm) => (
                  <SelectItem key={tm.email} value={tm.email}>
                    {tm.name || tm.email}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
          <Select value={selectedRole} onValueChange={(v) => setSelectedRole(v as 'member' | 'lead')}>
            <SelectTrigger data-testid="member-role-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="member">Member</SelectItem>
              <SelectItem value="lead">Lead</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={handleAdd}
              disabled={!selectedEmail || selectedEmail === '__none__' || addMember.isPending}
              data-testid="confirm-add-member"
            >
              Add
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setIsAdding(false);
                setSelectedEmail('');
                setSelectedRole('member');
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <Button
          variant="outline"
          size="sm"
          onClick={() => setIsAdding(true)}
          className="w-full"
          data-testid="add-project-member-btn"
        >
          <Plus className="w-4 h-4 mr-1" />
          Add Team Member
        </Button>
      )}

      {/* Remove confirmation */}
      <AlertDialog open={!!removeTarget} onOpenChange={(open) => !open && setRemoveTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove team member?</AlertDialogTitle>
            <AlertDialogDescription>
              Remove {removeTarget?.user_name || removeTarget?.user_email} from this project?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRemove} data-testid="confirm-remove-member">
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
